import React, {useMemo, useReducer, useState} from 'react'
import PropTypes from 'prop-types';
import Tr from 'translations/Translations';
import {makeStyles} from "@material-ui/core/styles";
import FolderIcon from "@material-ui/icons/Folder";
import WorkableIconEditButton from "components/button/WorkableIconEditButton";
import SimpleTable from "components/complex/simpleTable/SimpleTable";
import WorkableIconDeleteButton from "components/button/WorkableIconDeleteButton";
import TextInput from "components/TextInput";
import Form from "components/Form";
import * as Yup from "yup";
import useId from 'hooks/useId';
import Confirm from "components/popup/confirm/Confirm";
import InputLimits from "constants/InputLimits";
import VS from "constants/ValidationShema";
import RepoService from "services/RepoService";
import RepoFileTable from "./RepoFileTable";
import iconWorksReducer from "libs/iconWorksReducer";
import WorkableIconConfirmButton from "components/button/WorkableIconConfirmButton";
import WorkableIconSearchButton from "../../../button/WorkableIconSearchButton";
import EditDescriptionPopup, {createEditDescriptionFile} from "components/popup/EditDescriptionPopup";
import {connect} from "react-redux";
import WorkableIconShareButton from "../../../button/WorkableIconShareButton";
import PrivilegeService from "../../../../services/PrivilegeService";
import {shareSystemsActive} from "../../../../libs/shareSystems";
import ShareSelectPopup, {sharePopupData} from "../../../popup/ShareSelectPopup";
import useSnackbar from "../../../../hooks/useSnackbar";
import RepoFolderTypes from "../../../../constants/RepoFolderTypes";

const useStyles = makeStyles(theme => ({
    headTable: {
        background: theme.palette.background.default,
        marginTop: theme.spacing(5)
    },
    cell: {
        padding: `${theme.spacing(1)}px ${theme.spacing(1)}px 0`,
        border: 'none'
    },
    icon: {
        marginRight: theme.spacing()
    },
    nameCell: {
        fontWeight: 700
    },
    descriptionCell: {
        fontStyle: 'italic',
        whiteSpace: 'pre-line'
    },
    buttonColumn: {
        ...theme.components.table.buttonColumn,
        verticalAlign: 'top'
    },
    iconColumn: {
        width: 25,
        '& .MuiSvgIcon-root': {
            fontSize: '2rem'
        }
    },

}));

const RepoFolder = (
    {
        demandId,
        folder,
        isPublished,
        disabled,
        formSubmit,
        demandHeader,
        demandPrivileges,
        onMoveToSiwz
    }) => {

    const classes = useStyles();

    const [nameEdited, setNameEdited] = useState(false);
    const [openConfirmDeleteFolder, setOpenConfirmDeleteFolder] = useState(false);
    const [editDescriptionFile, setEditDescriptionFile] = useState(null);
    const [shareFilePopup, setShareFilePopup] = useState(null);
    const {enqueueSuccessSnackbar} = useSnackbar();
    const showShare = useMemo(() => demandPrivileges.INTEGRATIONS_UPLOAD && shareSystemsActive(demandHeader).length > 0, [demandHeader, demandPrivileges])
    const [iconWorks, dispatchIconWorks] = useReducer(iconWorksReducer, {
        anyWorks: false,
        name: false,
        delete: false,
        share: false,
    });
    const curDisabled = disabled || iconWorks.anyWorks;
    const nameFormId = useId();

    const handleSubmit = () => {
        formSubmit[nameFormId]();
    }

    const onSubmit = (values) => {
        dispatchIconWorks({type: 'name', works: true});
        RepoService.updateFolderName(demandId, folder.identity, values.name).then(() => {
            dispatchIconWorks({type: 'name', works: false});
            setNameEdited(false);
        });
    };


    const handleDeleteFolder = () => {
        dispatchIconWorks({type: 'delete', works: true});
        setOpenConfirmDeleteFolder(false);
        RepoService.deleteFolder(demandId, folder.identity).catch(() => dispatchIconWorks({type: 'delete', works: false}));
    };

    const openEditDescription = () => {
        setEditDescriptionFile(createEditDescriptionFile(demandId, folder.identity,
            folder.name, folder.description, curDisabled, false, true));
    };

    const onShareFolder = () => {
        setShareFilePopup(sharePopupData(folder.name, folder));
    }

    const doShareFolder = (row, system) => {
        setShareFilePopup(null);
        dispatchIconWorks({type: 'share', works: true});
        RepoService.shareRepoFolder(demandId, row.identity, system).then((resp) => {
            if (resp)
                enqueueSuccessSnackbar(Tr("int_upload_success_x", {x: system}, true))
        }).finally(() => {
            dispatchIconWorks({type: 'share', works: false})
        });
    }

    const getItems = () => {

        const actions = folder.actions || {};


        const rows = [];
        rows.push([
            <FolderIcon color="primary" className={classes.icon}/>,
            {
                value: nameEdited ?
                    <Form
                        initialValues={{
                            name: folder.name,
                        }}
                        validationSchema={Yup.object().shape({
                            name: VS.stringMax(InputLimits.procedure.documents.NAME),
                        })}
                        onSubmit={onSubmit}
                        id={nameFormId}
                    >
                        {(formikProps) => (
                            <TextInput
                                id='name'
                                formikProps={formikProps}
                                label={Tr('name')}
                                limit={InputLimits.procedure.documents.NAME}
                            />
                        )}
                    </Form>
                    : folder.name,
                className: classes.nameCell
            },
            showShare && folder.type !== RepoFolderTypes.Z_TRASH ? <WorkableIconShareButton
                tooltip={`${Tr("send_folder")} `}
                onClick={onShareFolder}
                disabled={curDisabled}
                working={iconWorks.share}
            /> : null,
            <WorkableIconSearchButton
                onClick={openEditDescription}
                tooltip={folder.description ? <p className={classes.descriptionTooltip}>{folder.description}</p> : <i>{Tr('no_description')}</i>}
            />,
            nameEdited ?
                <WorkableIconConfirmButton
                    color='primary'
                    onClick={() => handleSubmit("name")}
                    disabled={curDisabled}
                    working={iconWorks.name}
                /> :
                <WorkableIconEditButton
                    tooltip={Tr('edit_name')}
                    onClick={() => setNameEdited(true)}
                    disabled={!actions.updateFolderName || curDisabled}
                    hideTooltip={!actions.updateFolderName}
                />,
            <WorkableIconDeleteButton
                tooltip={actions.deleteFolder ? Tr('delete_folder') : Tr('delete_repo_folder_disabled')}
                onClick={() => setOpenConfirmDeleteFolder(true)}
                disabled={!actions.deleteFolder || curDisabled}
                working={iconWorks.delete}
            />
        ]);
        return rows;
    };
    return (
        <>
            <SimpleTable
                items={getItems()}
                columnClasses={[
                    classes.iconColumn,
                    undefined,
                    classes.buttonColumn,
                    classes.buttonColumn,
                    classes.buttonColumn,
                    classes.buttonColumn,
                ]}
                cellClassName={classes.cell}
                className={classes.headTable}
            />

            <RepoFileTable
                demandId={demandId}
                isPublished={isPublished}
                folder={folder}
                disabled={curDisabled}
                showShare={showShare}
                onMoveToSiwz={onMoveToSiwz}
            />

            {Boolean(editDescriptionFile) &&
                <EditDescriptionPopup
                    editDescriptionFile={editDescriptionFile}
                    onUpdated={() => setEditDescriptionFile(null)}
                    onClose={() => setEditDescriptionFile(null)}
                    service={{updateDescription: RepoService.updateFolderDescription}}
                />
            }
            {!!shareFilePopup && <ShareSelectPopup
                data={shareFilePopup}
                onCancel={() => setShareFilePopup(null)}
                onChoose={doShareFolder}
            />}

            <Confirm
                open={openConfirmDeleteFolder}
                onConfirm={() => handleDeleteFolder()}
                onCancel={() => setOpenConfirmDeleteFolder(false)}
                confirmText="confirm_delete_folder"
            />
        </>

    );
};
RepoFolder.propTypes = {
    demandId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    isPublished: PropTypes.bool.isRequired,
    disabled: PropTypes.bool,
    folder: PropTypes.shape({
        identity: PropTypes.number.isRequired,
        type: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        description: PropTypes.string,
        actions: PropTypes.object.isRequired,
        files: PropTypes.array.isRequired
    }).isRequired
}

export default connect((state) => ({
    formSubmit: state.form,
    demandPrivileges: state.privileges[PrivilegeService.DEMAND_PRIVILEGE_STORE_KEY],
    demandHeader: state.resources.demandHeader
}), {})(RepoFolder);